// src/pages/InvestigationsClinic/index.js
//
// Tetkikler — klinik geneli tetkik (laboratuvar + görüntüleme) listesi. Düz liste
// (docs/design-principles.md §3): arama + tip filtresi → durum çipleri →
// çerçevesiz tablo. Satıra tıklayınca sağdan açılan drawer'da InvestigationEditor.
// Düzenleme yetkisi "investigations:manage".

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { MenuItem } from "@mui/material";
import { Science as ScienceIcon } from "@mui/icons-material";

import InvestigationEditor from "../../components/organisms/PatientWorkspaceTabs/InvestigationEditor";
import useAuth from "../../hooks/useAuth";
import useTerms from "../../hooks/useTerms";
import { useHasPermission } from "../../hooks/useHasPermission";
import { useAlert } from "../../context/AlertProvider";
import { useClinicTheme } from "../../theme";
import { useListClinicTetkikMutation } from "../../store/secretaryApi";
import { useListLabTestsMutation, useListImagesMutation } from "../../store/systemVariablesApi";
import { FormSelect } from "../../components/atoms";
import {
  ClinicRecordListLayout,
  RecordEditorDrawer,
  SORTERS,
  patientNameOf,
  useClientList,
} from "../../components/organisms/ClinicRecordList";
import { fullDateTime } from "../Secretary/PatientsPage/patientListFormat";
import InvestigationsTable from "./InvestigationsTable";
import { STATUS_META, TYPE_LABELS, TYPE_OPTIONS, getStatusKey, itemNames } from "./constants";

const SEGMENTS = [
  { value: "all", label: "Tümü" },
  ...Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
];

const SEGMENT_EMPTY = {
  requested: "Sonuç bekleyen istem yok.",
  in_progress: "Şu an yapılmakta olan tetkik yok.",
  completed: "Tamamlanmış tetkik yok.",
  cancelled: "İptal edilmiş tetkik yok.",
};

const matches = (t, text) =>
  patientNameOf(t.patient).toLocaleLowerCase("tr").includes(text) ||
  String(t.patient?.tcNo || "").includes(text) ||
  itemNames(t).some((n) => n.toLocaleLowerCase("tr").includes(text));

const SORT_FNS = { name: SORTERS.name((t) => t.patient), date: SORTERS.date((t) => t.createdAt) };
const DEFAULT_DIR = { name: "asc", date: "desc" };

export default function InvestigationsClinic() {
  const terms = useTerms();
  const { auth } = useAuth();
  const token = auth?.accessToken;
  const { palette } = useClinicTheme();
  const { showError } = useAlert();
  const canManage = useHasPermission("investigations:manage");

  const [listClinicTetkik, { isLoading }] = useListClinicTetkikMutation();
  const [listLabTests] = useListLabTestsMutation();
  const [listImages] = useListImagesMutation();

  const [records, setRecords] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [images, setImages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [typeFilter, setTypeFilter] = useState("");

  const refresh = useCallback(async () => {
    if (!token) return;
    try {
      const res = await listClinicTetkik({ token }).unwrap();
      setRecords(res?.data?.list || []);
    } catch (_) {
      showError("Tetkikler yüklenemedi.");
    } finally {
      setLoaded(true);
    }
  }, [token, listClinicTetkik, showError]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Lab + görüntü seçeneklerini editor için yükle
  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await listLabTests({ token, status: "active", limit: 500 }).unwrap();
        setLabTests(res?.data?.labTests || res?.data?.items || []);
      } catch (_) {
        /* sessiz */
      }
    })();
  }, [token, listLabTests]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await listImages({ token, status: "active", limit: 500 }).unwrap();
        setImages(res?.data?.images || res?.data?.items || []);
      } catch (_) {
        /* sessiz */
      }
    })();
  }, [token, listImages]);

  // Tip filtresi listeyi önceden daraltır; durum çiplerinin sayıları buna göre
  const typed = useMemo(
    () => (typeFilter ? records.filter((r) => r.type === typeFilter) : records),
    [records, typeFilter]
  );

  const list = useClientList(typed, {
    segmentOf: getStatusKey,
    matches,
    sorters: SORT_FNS,
    defaultDir: DEFAULT_DIR,
    initialSort: "date",
  });

  const segments = useMemo(
    () => SEGMENTS.map((s) => ({ value: s.value, label: `${s.label} · ${list.counts[s.value] || 0}` })),
    [list.counts]
  );

  const handleSaved = useCallback((updated) => {
    if (!updated?._id) return;
    setRecords((prev) => prev.map((r) => (r._id === updated._id ? { ...r, ...updated, patient: r.patient } : r)));
    setSelected((prev) => (prev && prev._id === updated._id ? { ...prev, ...updated, patient: prev.patient } : prev));
  }, []);

  const handleEditorError = useCallback(
    (err) => {
      const status = err?.status || err?.originalStatus;
      const serverMsg = err?.data?.message;
      if (status === 423) {
        showError(serverMsg || `Bu ${terms.patientLower} şu an ${terms.examLoc} — değişiklik kaydedilemedi.`);
        return;
      }
      showError(serverMsg || "Tetkik kaydedilemedi.");
    },
    [showError, terms]
  );

  const empty = list.searchText
    ? { title: "Tetkik bulunamadı", message: `“${list.searchText}” aramanızla eşleşen tetkik yok. Aramayı değiştirin.` }
    : list.segment !== "all"
    ? { title: "Bu filtrede tetkik yok", message: SEGMENT_EMPTY[list.segment] }
    : typeFilter
    ? { title: "Tetkik bulunamadı", message: `${TYPE_LABELS[typeFilter]} tetkiki yok.` }
    : { title: "Henüz tetkik yok", message: `Tetkikler ${terms.patientLower} detayındaki Tetkik sekmesinden istenir.` };

  return (
    <>
      <ClinicRecordListLayout
        icon={<ScienceIcon sx={{ fontSize: 22 }} />}
        title="Tetkikler"
        subtitle="Laboratuvar ve görüntüleme istemleri, sonuç durumu"
        search={list.search}
        onSearchChange={list.setSearch}
        searchPlaceholder={`${terms.patient} adı, TC no veya test adı ile ara`}
        filters={
          <FormSelect
            palette={palette}
            value={typeFilter}
            onChange={(v) => {
              setTypeFilter(v);
              list.setSegment(list.segment);
            }}
            placeholder={null}
            fullWidth={false}
            sx={{ width: 180 }}
          >
            {TYPE_OPTIONS.map((t) => (
              <MenuItem key={t.value || "all"} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </FormSelect>
        }
        segments={segments}
        segment={list.segment}
        onSegmentChange={list.setSegment}
        segmentAriaLabel="Tetkik durumu"
      >
        <InvestigationsTable list={list} isLoading={isLoading && !loaded} empty={empty} onOpenDetail={setSelected} />
      </ClinicRecordListLayout>

      <RecordEditorDrawer
        open={Boolean(selected)}
        onClose={() => setSelected(null)}
        icon={<ScienceIcon />}
        title={selected ? patientNameOf(selected.patient) : "Tetkik"}
        subtitle={selected ? `${TYPE_LABELS[selected.type] || "Tetkik"} · ${fullDateTime(selected.createdAt)}` : ""}
        readOnly={!canManage}
        patientLink={selected?.patient?._id ? `/Hastalar/${selected.patient._id}?tab=tetkik` : null}
      >
        {selected && (
          <InvestigationEditor
            key={selected._id}
            record={selected}
            mode="standalone"
            patientId={selected.patient?._id}
            token={token}
            canEdit={canManage}
            labTests={labTests}
            images={images}
            onSaved={handleSaved}
            onError={handleEditorError}
          />
        )}
      </RecordEditorDrawer>
    </>
  );
}
